import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ITable } from '../../interfaces/table';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, share } from 'rxjs/operators';
import { WebsocketService } from '../websocket/websocket.service';
import { NotifierService } from 'angular-notifier';
import { AuthService } from '../auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { Inbound, WidgetMessageData } from '../../interfaces/websocket';

@Injectable({
  providedIn: 'root',
})
export class TableService {
  public loading = true;
  public items: ITable[] = [];

  public language = localStorage.getItem('lang') ?? this.translate.currentLang;

  constructor(
    public auth: AuthService,
    private http: HttpClient,
    private translate: TranslateService,
    private websocket: WebsocketService,
    private notifier: NotifierService,
  ) {}

  public fetchAll(id: number): Observable<ITable[]> {
    const request = this.http.get<ITable[]>(`/restaurants/${id}/tables`).pipe(share());

    request.subscribe((response) => {
      this.loading = false;
      this.items = response;
      this.sort();
    }, (err) => {
      console.log(err);
    });

    return request;
  }

  websocketInit(): void {
    this.websocket.subscribe((message: Inbound) => {
      switch (message.type) {
        case 'echo':
          this.websocketOnEcho(message.data);
          break;
        case 'loginSuccessful':
          this.websocketOnLoginSuccessful();
          break;
        case 'deleteTable':
        case 'addTable':
        case 'updateTable':
          this.fetchAll(message.data.restaurantId);
          break;
        case 'updateTable.field':
          const tableId = message.data.tableId;
          const data = message.data;

          switch (data.field) {
            case 'batteryLevel':
              this.websocketOnBatteryLevel(
                +tableId,
                data.value.level,
                data.value.charging,
                data.value.updatedAt
              );
              break;
            case 'online':
              this.websocketOnConnectionStatus(
                +tableId,
                data.value
              );
              break;
            case 'deviceUuid':
              this.websocketOnDeviceUuid(
                +tableId,
                data.value
              );
              break;
            default:
              break;
          }
          break;
        case 'attachTablesToWaiter':
          this.fetchAll(message.data.attachedTables[0].restaurantId);
          break;
        case 'detachTablesFromWaiter':
          this.fetchAll(message.data.detachedTables[0].restaurantId);
          break;
        case 'tablesList':
          this.loading = true;
          this.items = message.data;
          this.loading = false;

          this.sort();
          break;
        default:
          break;
      }
    });

    this.websocket.connected$.pipe(
      distinctUntilChanged(),
      filter((status) => !status)
    ).subscribe(() => {
      this.items.map((table) => {
        table.online = undefined;

        this.setTableStatus({
          tableId: table.id,
          statusMain: false,
          statusSecondary: false,
          statusTertiary: false,
        });
      });

      this.sort();
    });
  }

  websocketOnEcho(data: any): void {
    if (data.tableId && this.isStatusAvailable(data)) {
      this.setTableStatus(data);
    }

    if (data.tableId && typeof data.playlistItems !== undefined) {
      const table = this.findById(data.tableId);

      if (table) {
        table.playlistItems = data.playlistItems;
      }
    }
  }

  isStatusAvailable(data: any): boolean {
    return (
      data.status !== undefined
      || data.pay !== undefined
      || data.statusMain !== undefined
      || data.statusSecondary !== undefined
      || data.statusTertiary !== undefined
    );
  }

  websocketOnConnectionStatus(id: number, status = true): void {
    const table = this.findById(id);

    if (table) {
      table.online = status;

      this.sort();
    }
  }

  public websocketOnDeviceUuid(id: number, deviceUuid: string | null): void {
    const table = this.findById(id);

    if (table) {
      table.deviceUuid = deviceUuid;

      if (table.deviceUuid === null) {
        table.online = false;
        table.statusMain = false;
        table.statusSecondary = false;
        table.statusTertiary = false;

        if (table.statusTimer) {
          clearTimeout(table.statusTimer);
        }
      }

      this.sort();
    }
  }

  websocketOnBatteryLevel(id: number, level: number | null, charging = false, updatedAt: string | null): void {
    const table = this.findById(id);

    if (table) {
      table.batteryLevel = { level, charging, updatedAt };
    }
  }

  private setTableStatus(data: any): void {
    this.items.forEach((table: any) => {
      if (table.id === data.tableId) {
        table.statusMain = (data.statusMain || data.status) ?? false;
        table.statusSecondary = (data.statusSecondary || data.pay) ?? false;
        table.statusTertiary = (data.statusTertiary || data.repeat) ?? false;

        if (table.statusTimer) {
          clearTimeout(table.statusTimer);
        }

        if (table.statusMain || table.statusSecondary || table.statusTertiary) {
          table.statusTimer = setTimeout(() => {
            this.setTableStatus({
              tableId: table.id,
              statusMain: false,
              statusSecondary: false,
              statusTertiary: false,
            });
          }, 10000);
        }

        this.sort();
      }
    });
  }

  websocketOnLoginSuccessful(): void {
    console.log('WS: account successful');
  }

  public sendMessage(table: ITable, data: WidgetMessageData): void {
    this.websocket.sendMessage('echoToWidget', {
      tableId: table.id,
      data
    });
  }

  public cancelCalled(table: ITable): void {
    return this.sendMessage(table, { status: false, statusMain: false });
  }

  public cancelCalledPay(table: ITable): void {
    return this.sendMessage(table, { pay: false, statusSecondary: false });
  }

  public cancelCalledRepeat(table: ITable): void {
    return this.sendMessage(table, { repeat: false, statusTertiary: false });
  }

  public requestPlaylistItems(table: ITable): void {
    this.sendMessage(table, {
      getPlaylistItems: true,
    });
  }

  public requestDeviceTurnOff(table: ITable): void {
    this.sendMessage(table, {
      deviceTurnOff: true,
    });
  }

  public sort(): void {
    this.items = [...this.items].sort((a, b) => {
      return (
        Number(!!b.deviceUuid) - Number(!!a.deviceUuid) ||
        Number(b.statusMain ?? false) - Number(a.statusMain ?? false) ||
        Number(b.statusSecondary ?? false) - Number(a.statusSecondary ?? false) ||
        Number(b.statusTertiary ?? false) - Number(a.statusTertiary ?? false) ||
        Number(b.online ?? false) - Number(a.online ?? false) ||
        a.id - b.id
      );
    });
  }

  public findById(id: number): ITable | undefined {
    return this.items.find((item) => item.id === id);
  }

  public remove(restaurantId: number, tableId: number): Observable<void> {
    const request = this.http.delete<void>(`/restaurants/${restaurantId}/tables/${tableId}`).pipe(
      share()
    );

    request.subscribe((response) => {
      this.notifier.notify('success', this.translate.instant('pages.tables.messages.deletedTable'));
    });

    return request;
  }
}
